import coreStore from '@/stores/core';
import performanceStore from '@/stores/performance';
import sessionStore from '@/stores/session';
import API from '@/services';
import { getSessionInfo } from '@shopee/shopee-chatbot-agent-chat/dist/getSessionInfo';
import { UserSessionStatus, UserSessionChannelType } from '@shopee/shopee-chatbot-agent-chat/dist/types';
import { throttle as lodashThrottle } from '@/utils/lodashReplacement';
import {
  LIVE_CONTEXT_DATA_KEY,
  LIVE_AGENT_DATA_KEY,
  isApp,
  CHATBOT_TYPE,
  VIRTUAL_CALL_NEED_RECOVER_HISTORY,
  GET_VIRTUAL_CALL_RECOVERY_CONTEXT,
} from '../constants';
import { getScrollElement, mktBridgeCallHandler } from './toolsWithoutDeps';
import { getLiveChatSDKHttpHeaders, getSDKToken, setTokenRemainTime, initLiveChatSDK } from '@/utils/liveChatToken';
import { getConfig } from '@/config/core';
import { App } from '@/config/constants';
import { getDataFromLocalStorage } from '../bridges/localStorage';
import { ARTICLE_DETAIL_HYPERLINK_TYPE } from '../tracking/constants';
import { AgentChat } from '@shopee/shopee-chatbot-agent-chat';
import { getLiveChatApiDomain, isSaasEntryPoint } from '../saas';
import { getSuffix } from '../locale';
import { getTrans } from './getTrans';
import { handleRequest } from '@/utils';
import chatbotCommonVariable from '@/utils/common';

export { getTrans } from './getTrans';

export const handleHeartbeat = (res: any, func: any) => {
  const { heartbeat_info } = performanceStore;

  if (res) {
    const { heart_beat_switch, intervals, token_expire_remain_time } = res;
    performanceStore.UPDATE_HEARTBEAT({
      flag: heart_beat_switch,
      delay: intervals * 1000,
      retryTimes: 1,
    });

    if (typeof token_expire_remain_time === 'number') {
      setTokenRemainTime(token_expire_remain_time);

      if (token_expire_remain_time < 10 * 60) {
        initLiveChatSDK();
      }
    }

    heart_beat_switch
      && setTimeout(() => {
        heart_beat_switch && func();
      }, intervals * 1000);
  } else {
    if (heartbeat_info.retryTimes > 0) {
      performanceStore.UPDATE_HEARTBEAT({ retryTimes: heartbeat_info.retryTimes - 1 });
    } else {
      performanceStore.UPDATE_HEARTBEAT({ flag: false });
      return;
    }

    heartbeat_info.flag
      && setTimeout(() => {
        heartbeat_info.flag && func();
      }, heartbeat_info.delay);
  }
};

// chatbot心跳请求
export const sendCBHeartbeat = async (init: any) => {
  if (coreStore.safeGetStore('agentStore').inAgent) {
    return;
  }

  if (init) {
    performanceStore.INIT_HEARTBEAT();
  }

  const { taskBotId, isTaskBotEnded } = sessionStore.taskBot as any;

  const res = await (API as any)
    .postHeartbeat({
      id: coreStore.safeGetStore('initStore').sessionId,
      bot_id: taskBotId || undefined,
    })
    .catch((err: any) => { });

  // task bot 结束后如果没有触发新的 task bot，则停止发送 bot_id 参数
  if (isTaskBotEnded) {
    sessionStore.UPDATE_TASK_BOT({ taskBotId: '' });
  }

  handleHeartbeat(res, sendCBHeartbeat);
};

export const getChildInject = (...args: string[]) => {
  return (allStores: any) => {
    const result: Record<string, any> = {};
    args.forEach((storeName) => (result[storeName] = allStores.coreStore.safeGetStore(storeName)));
    return result;
  };
};

export const getContent = (option: any) => {
  const { text_key, text_value } = option;

  return (text_value || getTrans(text_key))?.toString();
};

export const afterDialogueShow = (from = '') => {
  // 不是用户发送 && 向上滚动超过一屏
  if (from && from !== 'user' && coreStore.safeGetStore('initStore').isScrollMaxThanOneScreen) {
    return;
  }
  if (coreStore.safeGetStore('showDataStore').dataListLength < 4) {
    return;
  }
  // if (inputStore.isInputFocus && isIos) {
  //   document.body.scrollTop = document.documentElement.scrollTop = document.body.scrollHeight;
  //   return;
  // }
  setTimeout(() => {
    ScrollToBottomWithDebounce();
  }, 200);
};

const ScrollToBottomWithDebounce = lodashThrottle(
  () => {
    // 消息列表滚到最尾
    scrollToBottom();
  },
  300,
  { trailing: true },
);

// [live-chat-split-change]
// 滚动到底部
export const scrollToBottom = (isImmediately = false) => {
  const scrollEle: any = getScrollElement();
  if (!scrollEle) {
    return;
  }

  const keyboardHeight
    = typeof window.visualViewport !== 'undefined'
      ? (window.visualViewport?.height as number)
      : document.body.offsetHeight - document.body.scrollTop; // ios
  const scrollHeight = coreStore.safeGetStore('inputStore').isInputFocus
    ? scrollEle.scrollHeight - keyboardHeight
    : scrollEle.scrollHeight - document.body.offsetHeight;
  try {
    const behaviorOptions = isImmediately ? {} : { behavior: 'smooth' };
    scrollEle.scroll({
      top: scrollHeight,
      left: 0,
      ...behaviorOptions,
    });
  } catch (err) {
    scrollEle.scrollTop = scrollHeight;
    scrollEle.scrollLeft = 0;
  }
};

export const scrollTo = (left = 0, top = 0) => {
  const scrollEle: any = getScrollElement();

  if (!scrollEle) {
    return;
  }

  try {
    scrollEle.scroll({
      top,
      left,
      behavior: 'smooth',
    });
  } catch (err) {
    scrollEle.scrollTop = top;
    scrollEle.scrollLeft = left;
  }
};

// 替换字符串中参数
export const replaceParams = (text: string, extraData: any) => {
  const reg = new RegExp(/\$\{(.+?)\}/g);
  const origin: any[] = [];
  let lastIndex = reg.lastIndex;
  let result: RegExpExecArray | null;
  while ((result = reg.exec(text)) != null) {
    origin.push(text.slice(lastIndex, result.index), extraData[result[1]]);
    lastIndex = reg.lastIndex;
  }
  origin.push(text.slice(lastIndex));

  return origin.every((ele) => typeof ele === 'string' || typeof ele === 'number') ? origin.join('') : origin;
};

// 调用JSBridge
export const bridgeCallFunc = (func: any): any => {
  // @ts-ignore
  const bridgeCall = (resolve: any) => {
    try {
      if (!window.bridgeInit || !window.bridgeRegisterHandler) {
        return setTimeout(() => bridgeCall(resolve), 300);
      }

      window.bridgeInit(() => {
        resolve(func());
      });
    } catch (err) {
      // do nothing
      // 注意: 走入此catch后，bridgeCallFunc返回的promise将一直pending
    }
  };

  return new Promise((resolve) => {
    try {
      bridgeCall(resolve);
    } catch (err) {
      console.error(err);
      resolve({});
    }
  });
};

export const isNum = (value: any) => {
  return typeof value === 'number';
};

export const getCookie = (e: any) => {
  const isBrowser = typeof window === 'object';
  if (isBrowser) {
    const reg = new RegExp('(?:^| )' + e + '=([^;]*)');
    const t = document.cookie.match(reg);
    if (t !== null) {
      return decodeURIComponent(t[1]);
    }
  }
  return '';
};

export const asyncReport = (
  msg: string,
  value: { level?: 'error' | 'warn' | 'debug' } & { [propsName: string]: string } = { level: 'error' },
) => {
  import('@/utils/report').then(({ default: reporter }) => {
    reporter.apiLog(msg, value);
  });
};

const requestRecoveryHistory = async () => {
  const [liveContextData, liveAgentData, virtualCallNeedRecoveryData] = await Promise.all([
    getLiveContextData(),
    getLiveAgentData(),
    getVirtualCallNeedRecoveryData(),
  ]);
  // 是否存在需要恢复的历史记录
  const storedContextData = liveContextData;
  let storedAgentData = liveAgentData;

  // 是否处于 virtual call 恢复场景 是的话 只要有 storedContextData 即可
  const virtualCallNeedRecoverHistory = virtualCallNeedRecoveryData;
  chatbotCommonVariable?.UPDATE_VIRTUAL_CALL_NEED_RECOVER_HISTORY(virtualCallNeedRecoverHistory === 'true', true);
  import('@/utils/report').then(({ default: reporter }) => {
    if (typeof reporter?.apiBatchReport === 'function') {
      reporter.apiBatchReport({
        type: 'count',
        value: { label: GET_VIRTUAL_CALL_RECOVERY_CONTEXT, value: virtualCallNeedRecoverHistory },
      });
    }
  });
  const haveVirtualCallNeedRecoverHistory = virtualCallNeedRecoverHistory === 'true';
  // 所以 当 有 storedContextData 的值时  且 haveVirtualCallNeedRecoverHistory 为 true 时 就不会进入 下面的 asyncReport
  if (haveVirtualCallNeedRecoverHistory && !storedAgentData) {
    storedAgentData = '{}';
  }
  if (!storedContextData || !storedAgentData) {
    asyncReport('Will not recover live chat: no storage', { level: 'debug' });
    return null;
  }

  // 解析数据
  let contextData: any = {};
  let agentData: any = {};
  try {
    contextData = JSON.parse(storedContextData);
    agentData = JSON.parse(storedAgentData);
  } catch (err) {
    console.log('err: ', err);
  }
  const { currentUser = {}, chatbotInitData = {}, sessionIDList = [] } = contextData;
  const { agentStoreData = {}} = agentData;
  const { app } = getConfig();
  const { chatbotUserId: newChatbotUserId } = getSDKToken();

  // 2022.8.30, shopee app下SPC_U可能为空，userId为更准确的用户是否相同的判断，使用userId替换Spc_u
  const isSameUserInShopee = currentUser.SPC_U === getCookie('SPC_U') || currentUser.userId === getCookie('userid');
  const isSameUserInLiveChatSDK
    = currentUser.chatbotUserId && newChatbotUserId && currentUser.chatbotUserId === newChatbotUserId;
  const isSameUser
    = {
      [App.Mitra]: isSameUserInLiveChatSDK,
      [App.Driver]: isSameUserInLiveChatSDK,
      [App.Partner]: isSameUserInLiveChatSDK,
      [App.Bank]: isSameUserInLiveChatSDK,
      [App.Shopee]: isSameUserInShopee,
      [App.ShopeeLite]: isSameUserInShopee,
      [App.Food]: isSameUserInLiveChatSDK,
    }[app] || false;

  // 判断是否当前用户的历史信息
  if (!isSameUser) {
    asyncReport('Will not recover live chat: not current user', { level: 'debug' });
    return null;
  }

  const isPremiumUserConnection
    = chatbotInitData.chatbotType === CHATBOT_TYPE.PREMIUM_CHATBOT
    || chatbotInitData.chatbotType === CHATBOT_TYPE.PREMIUM_LIVE_CHAT;

  const getBubbleSessionInfoPromise = proxyGetSessionInfoFromBubble({ sessionKey: agentStoreData.sessionKey });
  const httpHeaders = getLiveChatSDKHttpHeaders();
  const isSaas = isSaasEntryPoint();
  if (isSaas) {
    const baseURL = getLiveChatApiDomain();
    new AgentChat({
      passGateway: {
        http: baseURL,
        websocket: baseURL,
      } as any,
    });
  }

  const preGetLiveChatUserSession = await window.preRequest?.preGetLiveChatUserSession;
  const getSessionInfoPromise = getSessionInfo(
    {
      from: 'h5',
      channel: isPremiumUserConnection
        ? UserSessionChannelType.PremiumUserChannel
        : UserSessionChannelType.ChatbotChannel,
    },
    httpHeaders,
  );

  const [bubbleSessionInfo, res] = await Promise.all([
    getBubbleSessionInfoPromise,
    preGetLiveChatUserSession || getSessionInfoPromise,
  ]);

  const recoveryHistoryFromBubble = {
    contextData,
    agentData,
    realTimeData: bubbleSessionInfo,
  };

  let userSessionExtensionInfo: any = {};

  if (!haveVirtualCallNeedRecoverHistory) {
    // 如果 是 virtual call 最小化 则需要恢复数据 所以不走之前的逻辑
    if (res instanceof Error || !res) {
      return bubbleSessionInfo && recoveryHistoryFromBubble; // 接口出错，以bubble的数据为准
    }

    const { isExist, status, sessionKey, userSessionExtensionInfo: resUserSessionExtensionInfo } = res;
    userSessionExtensionInfo = resUserSessionExtensionInfo;

    if (!isExist || status === UserSessionStatus.ended) {
      asyncReport(`Session ended when back to chatbot;bubbleInfo:${JSON.stringify(bubbleSessionInfo)}`, { level: 'debug' });
      return bubbleSessionInfo && recoveryHistoryFromBubble; // 会话结束，当用户退出页面但没退出APP时，返回页面时仍然需要恢复对话历史
    }

    if (status === UserSessionStatus.initial) {
      asyncReport('Will not recover live chat: session status is initail', { level: 'debug' });
      return null;
    }

    if (sessionKey !== agentStoreData.sessionKey) {
      // 存储的会话与用户最新会话不一致，直接返回
      asyncReport('Will not recover live chat: sessionKey is not same', { level: 'debug' });
      return null;
    }
  } else {
    const preGetVirtualCallStatusCheck = await window.preRequest?.preGetVirtualCallStatusCheck;
    const checkData
      = preGetVirtualCallStatusCheck
      || (await handleRequest('virtualCallStatusCheck', {
        // 检查 session 和 vc 状态
        session_id: contextData?.chatbotInitData?.sessionId,
      }));
    const { session_expired, vc_in_progress } = checkData ?? {};
    if (session_expired || !vc_in_progress) {
      // session 过期 或 不在 vc 过程中 就不使用缓存 同时设置 vc 标识为 false
      chatbotCommonVariable?.UPDATE_VIRTUAL_CALL_NEED_RECOVER_HISTORY(false);
      // saveDataToLocalStorage(VIRTUAL_CALL_NEED_RECOVER_HISTORY, 'false');
      return null;
    }
  }

  if (!isApp) {
    return {
      contextData,
      agentData,
      realTimeData: {
        position: 0,
        unreadMsgs: 0,
        hasShowRating: false,
      },
      sessionIDList,
    };
  }

  return {
    contextData,
    agentData,
    realTimeData: {
      position: +(userSessionExtensionInfo.queue_position ?? bubbleSessionInfo?.position ?? 0),
      unreadMsgs: bubbleSessionInfo?.unreadMsgs ?? 0,
      hasShowRating: bubbleSessionInfo?.hasShowRating,
    },
    sessionIDList,
  };
};

const cacheProxy = (fn: (...params: any[]) => any, millisecondTime = Number.MAX_SAFE_INTEGER) => {
  let cache: any;
  let lastTime = Date.now();

  return async (...args: any[]) => {
    const isCacheValid = Date.now() - lastTime < millisecondTime;

    if (isCacheValid && cache) {
      return await cache;
    }

    cache = fn.apply(this, args);
    lastTime = Date.now();

    return cache;
  };
};

export const getRecoveryHistory = cacheProxy(requestRecoveryHistory, 10000);

export const getLiveContextData = cacheProxy(async () => {
  return await getDataFromLocalStorage(LIVE_CONTEXT_DATA_KEY);
}, 10000);

export const getLiveAgentData = cacheProxy(async () => {
  return await getDataFromLocalStorage(LIVE_AGENT_DATA_KEY);
}, 10000);

export const getVirtualCallNeedRecoveryData = cacheProxy(async () => {
  return await getDataFromLocalStorage(VIRTUAL_CALL_NEED_RECOVER_HISTORY);
}, 10000);

export const getSessionInfoFromBubble = async ({ sessionKey }: {
  sessionKey: string;
}): Promise<null | {
  position: string;
  unreadMsgs: number;
  hasShowRating: boolean;
}> => {
  if (!isApp) {
    return null;
  }

  return await bridgeCallFunc(async () => {
    // 获取bubble当前状态以及是否弹过rating
    const checkFeedbackHasShowRes = await mktBridgeCallHandler('sendDataToFloatingChatBubble', {
      chatData: JSON.stringify({
        action: 'checkFeedbackHasShow',
        data: { session_key: sessionKey }, // native 对比最后保存的 session_key，因为只会从 web 开始新的 chat，native 保存最后一个show过的rating对应key来比较并返回就行
      }),
    }).catch(() => ({}));
    const { error, data } = checkFeedbackHasShowRes as any;

    // bridge失败，不处理
    if (error !== 0) {
      console.warn('bridge error', error);
      asyncReport('May not recover live chat: call bridge error', { level: 'debug' });
      return null;
    }

    const {
      show, // 当前bubble状态是否显示
      chat_status, // 0(in-queue) / 1(in-chat)
      status_num, // (queue-seq-num / unread-msg-num)
      has_show, // 是否弹过 rating
    } = data;

    // 当前 bubble 已隐藏，则表示当前会话已完全结束，不需要恢复聊天历史
    if (!show) {
      console.info('bubble had showed');
      asyncReport('May not recover live chat: bubble is not showed', { level: 'debug' });
      return null;
    }
    console.info('bubble info', data);
    return {
      position: chat_status === 0 ? status_num : 0,
      unreadMsgs: chat_status === 1 ? status_num : 0,
      hasShowRating: has_show,
    };
  });
};

export const proxyGetSessionInfoFromBubble = cacheProxy(getSessionInfoFromBubble, 10000);

export const getFileExtension = (filename: string) => filename?.split('.')?.pop()?.toUpperCase();

export const getHyperlinkType = (element: HTMLLinkElement) => {
  const href = element?.href;
  console.log(' in app ', href, isInAppLink(href));
  return isInAppLink(href)
    ? ARTICLE_DETAIL_HYPERLINK_TYPE.IN_APP
    : isFormUrlLink(href)
      ? ARTICLE_DETAIL_HYPERLINK_TYPE.FORM_URL
      : href.startsWith('https')
        ? ARTICLE_DETAIL_HYPERLINK_TYPE.HTTPS
        : ARTICLE_DETAIL_HYPERLINK_TYPE.HTTP;
};

export const isInAppLink = (link: string) => getAppLink(link) != null;
export const getAppLink = (link: string) => {
  // 是否返回的是 rn 或 native 链接
  const regx = /^in-app:(r?n)\/(\S+)$/;
  const splitInAppLink = regx.exec(link);
  return splitInAppLink;
};

export const isIntentLink = (link: string) => getIntentLink(link) != null;
export const getIntentLink = (link = '') => {
  const regx = /^intent:/;
  const result = regx.exec(link);
  if (result === null) {
    return result;
  } else {
    return link.replace(regx, '');
  }
};


export const getNonShopeeAppLink = (link: string) => {
  // 是否返回的是 rn 或 native 链接
  const regx = /^in-app:(\S+)$/;
  const splitInAppLink = regx.exec(link);
  return splitInAppLink;
};

export const isFormUrlLink = (link: string) => getFormUrlLink(link) != null;
export const getFormUrlLink = (link = '') => {
  const regx = /^form-url:/;
  const result = regx.exec(link);
  if (result === null) {
    return result;
  } else {
    return link.replace(regx, '');
  }
};
